import React, { useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../framework/redux/hooks";
import { removeWorkShiftTimeSlot } from "../../framework/redux/slices/workShiftTimeSlotSlice";
import { DailyWorklogTitle } from "./dailyWorklogTitle";
import { WorkTimeEntryCard } from "./workTimeEntryCard";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { Base } from "../../framework/common/base";
import {
    deleteCategoryFromWorkShiftTimeSlot,
    deleteWorkShiftTimeSlot,
} from "../../services/workShiftTimeSlotService";
import { useTranslation } from "react-i18next";
import { addSuccessMsg } from "../../framework/redux/slices/appSlice";
import { handleApiError } from "../../services/baseService";
import { ConfirmType, useConfirm } from "../../hooks/framework/useConfirm";
import { useFilterWorkShiftTimeSlotsByPlannedShiftStart } from "../../hooks/worktime/useSelectWorkShiftTimeSlots";
import { useParams } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/DeleteForever";
import { useAsyncThunkAction } from "../../hooks/framework/useAsyncThunkAction";
import {
    fetchDayBookings,
    fetchDayBookingTypes,
    removeDayBooking,
} from "../../framework/redux/slices/dayBookingSlice";
import { useSelectDaybookingsByDateRange } from "../../hooks/worktime/useSelectDayBookings";
import { DayBookingEntryCard } from "./DayBookingEntryCard";
import { fetchWorkShifts } from "../../framework/redux/slices/workShiftSlice";
import { calculatedWorkHours } from "../../framework/redux/slices/workHoursSlice";
import {
    useSelectFillingHoursAmountByDate,
    useSelectDailyAllowancesByWorkShiftIds,
    useSelectPaidWorkHoursAmountByWorkShiftIds,
    useSelectDeductionWorkHoursAmountByWorkShiftIdsGroupedByCategory,
    useSelectPaidWorkHoursAmountByDate,
} from "../../hooks/worktime/useSelectWorkHours";
import { WorkShiftTimeSlotState } from "../../models/common/enums";
import { getWorkShiftTimeSlotDurationMinutes } from "../../models/workShiftTimeSlot/workShiftTimeSlot";
import { WorkHourCard } from "./WorkHourCard";
import { useSelectSalaryPeriodByDate } from "../../hooks/worktime/useSelectSalaryPeriod";
import { fetchSalaryPeriods } from "../../framework/redux/slices/salaryPeriodSlice";
import { useReload } from "../../hooks/framework/useReload";
import { fetchPlannedWorkShifts } from "../../framework/redux/slices/plannedWorkShiftSlice";
import { useSelectPlannedWorkShiftsByDate } from "../../hooks/worktime/useSelectPlannedWorkShifts";
import { PlannedWorkShiftCard } from "./PlannedWorkShiftCard";
import { DailyWorklogDate } from "./dailyWorklogDate";
import { useAppOptionValueEquals } from "../../hooks/framework/useAppOptionValue";
import { OptionWorkShiftPlanningEnabled } from "../../models/common/consts";
import {
    dayBookingToEntry,
    workHourToDayBookingEntry,
} from "../../models/dayBooking/dayBooking";
import { fetchCostCenters } from "../../framework/redux/slices/costCenterSlice";
import { fetchVehicles } from "../../framework/redux/slices/vehicleSlice";
import { useShowWorkShiftEndTimesInMobile } from "../../hooks/framework/useShowWorkShiftEndTimesInMobile";
import { useSelectWorkShiftsByDate } from "../../hooks/worktime/useSelectWorkShifts";

export const DailyWorklogMain = () => {
    const { date } = useParams();
    const [reloadKey, reload] = useReload();
    const dateString = Base.dateToDateJSON(date);
    const dayBookingTypes = useAppSelector((state) => state.dayBooking.dayBookingTypes);
    const costCenters = useAppSelector((state) => state.costCenter.costCenterItems);
    const vehicles = useAppSelector((state) => state.vehicle.idTitles);
    const showEndTimes = useShowWorkShiftEndTimesInMobile();
    const workShiftTimeSlots = useFilterWorkShiftTimeSlotsByPlannedShiftStart(
        dateString,
        dateString
    );

    const shiftInProgress = workShiftTimeSlots.some(
        (s) => s.state === WorkShiftTimeSlotState.InProgress
    );

    const shifts = useSelectWorkShiftsByDate(dateString, dateString);

    const workShiftIds = useMemo(
        () => shifts?.map((s) => s.workShiftId),
        [shifts]
    );

    const anyShiftIsSplit = useMemo(
        () => shifts?.some((s) => !!s.splitAt),
        [shifts]
    );

    const dayAllowances = useSelectDailyAllowancesByWorkShiftIds(
        workShiftIds
    );

    const dayWorkHourAmountFromShifts =
        useSelectPaidWorkHoursAmountByWorkShiftIds(workShiftIds, false);

    const dayWorkHourAmountWithoutShifts = useSelectPaidWorkHoursAmountByDate(
        dateString,
        dateString,
        false,
        true
    );

    const dayDeductions =
        useSelectDeductionWorkHoursAmountByWorkShiftIdsGroupedByCategory(
            workShiftIds,
            false
        );

    const dayFillingHours = useSelectFillingHoursAmountByDate(
        dateString,
        dateString,
        false
    );

    const [plannedShiftsEnabled] = useAppOptionValueEquals(
        OptionWorkShiftPlanningEnabled,
        true
    );

    const plannedShifts = useSelectPlannedWorkShiftsByDate(
        dateString,
        dateString
    );

    const dayMinutes = useMemo(
        () =>
            (shiftInProgress
                ? workShiftTimeSlots
                    .map((i) => getWorkShiftTimeSlotDurationMinutes(i))
                    .reduce((acc, cur) => acc + cur, 0)
                : (dayWorkHourAmountFromShifts ?? 0)) +
            (dayWorkHourAmountWithoutShifts ?? 0),
        [
            shiftInProgress,
            dayWorkHourAmountFromShifts,
            dayWorkHourAmountWithoutShifts,
            workShiftTimeSlots,
        ]
    );

    const selectedSalaryPeriodItem = useSelectSalaryPeriodByDate(dateString);
    const prevDaySalaryPeriodItem = useSelectSalaryPeriodByDate(
        Base.dateToDateJSON(Base.dateAddDays(dateString, -1))
    );
    const nextDaySalaryPeriodItem = useSelectSalaryPeriodByDate(
        Base.dateToDateJSON(Base.dateAddDays(dateString, 1))
    );

    const dayBookings = useSelectDaybookingsByDateRange(dateString, dateString);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const confirm = useConfirm();

    useAsyncThunkAction(
        () =>
            fetchWorkShifts({
                startDate: dateString,
                endDate: dateString,
            }),
        {
            onError: handleApiError,
        },
        [dateString, reloadKey]
    );

    useAsyncThunkAction(
        () =>
            plannedShiftsEnabled
                ? fetchPlannedWorkShifts({
                    startDate: dateString,
                    endDate: dateString,
                })
                : null,
        {
            onError: handleApiError,
        },
        [dateString, plannedShiftsEnabled]
    );

    useAsyncThunkAction(
        () => {
            if (
                anyShiftIsSplit &&
                nextDaySalaryPeriodItem?.startDate &&
                nextDaySalaryPeriodItem?.endDate &&
                nextDaySalaryPeriodItem.id !== selectedSalaryPeriodItem?.id
            ) {
                return calculatedWorkHours({
                    salaryPeriodId: nextDaySalaryPeriodItem?.id,
                    startDate: nextDaySalaryPeriodItem?.startDate,
                    endDate: nextDaySalaryPeriodItem?.endDate,
                });
            }
        },
        { onError: handleApiError },
        [anyShiftIsSplit, nextDaySalaryPeriodItem?.id, reloadKey]
    );

    useAsyncThunkAction(
        () => {
            if (
                anyShiftIsSplit &&
                prevDaySalaryPeriodItem?.startDate &&
                prevDaySalaryPeriodItem?.endDate &&
                prevDaySalaryPeriodItem.id !== selectedSalaryPeriodItem?.id
            ) {
                return calculatedWorkHours({
                    salaryPeriodId: prevDaySalaryPeriodItem?.id,
                    startDate: prevDaySalaryPeriodItem?.startDate,
                    endDate: prevDaySalaryPeriodItem?.endDate,
                });
            }
        },
        { onError: handleApiError },
        [anyShiftIsSplit, prevDaySalaryPeriodItem?.id, reloadKey]
    );

    useAsyncThunkAction(
        () => {
            if (
                selectedSalaryPeriodItem?.startDate &&
                selectedSalaryPeriodItem?.endDate
            ) {
                return calculatedWorkHours({
                    salaryPeriodId: selectedSalaryPeriodItem?.id,
                    startDate: selectedSalaryPeriodItem?.startDate,
                    endDate: selectedSalaryPeriodItem?.endDate,
                });
            }
        },
        { onError: handleApiError },
        [selectedSalaryPeriodItem?.id, reloadKey]
    );

    useAsyncThunkAction(
        () => fetchSalaryPeriods(),
        { onError: handleApiError },
        []
    );

    const overlappingWorkShiftTimeSlotItemIds = useMemo(() => {
        if (workShiftTimeSlots) {
            return workShiftTimeSlots
                .filter(
                    (item) =>
                        !item.isCustomType &&
                        workShiftTimeSlots.some(
                            (item2) =>
                                !item2.isCustomType &&
                                Base.isOverlapping(
                                    item.startDate,
                                    item.endDate,
                                    item2.startDate,
                                    item2.endDate
                                ) &&
                                item.id !== item2.id
                        )
                )
                .map((item) => item.id);
        }
    }, [workShiftTimeSlots]);

    const handleDeleteWorkShiftTimeSlot = (id: string) => {
        confirm({
            icon: DeleteIcon,
            title: t("workTime.workTimeDeleteConfirmation"),
            submitText: t("common.continue"),
            cancelText: t("common.cancel"),
            type: ConfirmType.Remove,
            onSubmit: () => {
                deleteWorkShiftTimeSlot(id)
                    .then(() => {
                        dispatch(
                            addSuccessMsg(
                                t("workTime.workTimeDeleteSuccessful")
                            )
                        );
                        dispatch(removeWorkShiftTimeSlot(id));
                    })
                    .catch(handleApiError)
                    .finally(reload);
            },
        });
    };

    const handleDeleteDayBooking = (id: string) => {
        const entry = dayBookingEntries.find(e => e.id === id);
        if (!entry) return;

        confirm({
            icon: DeleteIcon,
            title: t("dayBooking.dayBookingDeleteConfirmation"),
            submitText: t("common.continue"),
            cancelText: t("common.cancel"),
            type: ConfirmType.Remove,
            onSubmit: () => {
                (entry.isAutomatic
                    ? deleteCategoryFromWorkShiftTimeSlot(
                        entry.workShiftTimeSlotId,
                        entry.id.split(".")[1],
                        false
                    )
                    : dispatch(removeDayBooking(id)).unwrap()
                )
                    .then(() =>
                        dispatch(
                            addSuccessMsg(
                                t("dayBooking.dayBookingDeleteSuccessful")
                            )
                        )
                    )
                    .catch(handleApiError)
                    .finally(reload);
            },
        });
    };

    useAsyncThunkAction(
        () =>
            fetchDayBookings({
                startDate: dateString,
                endDate: dateString,
            }),
        {
            onError: handleApiError,
        },
        [date]
    );

    useAsyncThunkAction(
        () => fetchDayBookingTypes(),
        { onError: handleApiError },
        []
    );

    useAsyncThunkAction(
        () => fetchCostCenters(),
        { onError: handleApiError },
        []
    );

    useAsyncThunkAction(() => fetchVehicles(), { onError: handleApiError }, []);

    const dayBookingEntries = useMemo(
        () => [
            ...(dayBookings ?? []).map(dayBookingToEntry),
            ...(dayAllowances?.map((d) =>
                workHourToDayBookingEntry(
                    d,
                    dayBookingTypes,
                    costCenters,
                    vehicles,
                    workShiftTimeSlots
                )
            ) ?? []),
        ],
        [
            dayBookings,
            dayAllowances,
            dayBookingTypes,
            costCenters,
            vehicles,
            workShiftTimeSlots,
        ]
    );

    const displayWorkShiftTimeSlots =
        workShiftTimeSlots && workShiftTimeSlots.length > 0;
    const displayDayBookings = dayBookingEntries?.length > 0;
    const displayDeductions =
        !shiftInProgress && Object.keys(dayDeductions).length > 0;
    const displayFillingHours = dayFillingHours > 0;

    const displayPlannedShifts =
        plannedShiftsEnabled && plannedShifts?.length > 0;

    return (
        <Box sx={{ p: 2 }}>
            <DailyWorklogDate date={date} />
            {displayPlannedShifts ? (
                <>
                    {plannedShifts.length === 1
                        ? <DailyWorklogTitle
                                title={t("workTime.workShift")}
                                minutes={showEndTimes ? plannedShifts[0].duration : 0}
                          />
                        : <DailyWorklogTitle
                                title={t("workTime.workShifts")}
                                minutes={showEndTimes ? plannedShifts.reduce((sum, shift) => sum + shift.duration, 0) : 0}
                          />
                        }
                    {[...plannedShifts]
                        .sort((a, b) => Base.dateCompare(a.startTime, b.startTime))
                        .map((s) => (
                            <PlannedWorkShiftCard key={s.id} shift={s} />
                        ))}
                </>
            ) : null}
            <DailyWorklogTitle
                title={t("workTime.workTime")}
                date={date}
                minutes={dayMinutes}
                showNewEntryButton={true}
                showAddBreakButton={true}
                hasWorkShiftTimeSlots={displayWorkShiftTimeSlots}
            />

            {displayWorkShiftTimeSlots
                ? workShiftTimeSlots.map((item) => (
                    <WorkTimeEntryCard
                        key={item.id}
                        date={date}
                        item={item}
                        handleDelete={handleDeleteWorkShiftTimeSlot}
                        overlapping={overlappingWorkShiftTimeSlotItemIds?.includes(
                            item.id
                        )}
                    />
                ))
                : null}

            {(displayWorkShiftTimeSlots || displayPlannedShifts) &&
            displayDayBookings ? (
                <Divider sx={{ borderColor: "gray.main", mb: 2 }} />
                ) : null}
            {displayDayBookings
                ? dayBookingEntries.map((item) => (
                    <DayBookingEntryCard
                        key={item.id}
                        item={item}
                        handleDelete={handleDeleteDayBooking}
                    />
                ))
                : null}
            {displayDeductions ? (
                <>
                    <Divider sx={{ borderColor: "gray.main", mb: 2 }} />
                    <Typography
                        variant="h4"
                        sx={{ color: "primary.dark", mb: 1 }}
                    >
                        {t("workTime.deductions")}:
                    </Typography>
                    {Object.keys(dayDeductions).map((categoryId) => (
                        <WorkHourCard
                            key={categoryId}
                            categoryId={categoryId}
                            amount={dayDeductions[categoryId]}
                        />
                    ))}
                </>
            ) : null}
            {displayFillingHours ? (
                <>
                    <Divider sx={{ borderColor: "gray.main", mb: 2 }} />
                    <Typography
                        variant="h4"
                        sx={{ color: "primary.dark", mb: 1 }}
                    >
                        {t("workTime.fillingHours")}:
                    </Typography>
                    <WorkHourCard
                        label={t("workTime.fillingHours")}
                        amount={dayFillingHours}
                    />
                </>
            ) : null}
        </Box>
    );
};
